import { useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { RadioButton } from "primereact/radiobutton";
import { Checkbox } from "primereact/checkbox";
import * as client from "../../../client";
import styles from "./ReprocessDialog.module.css";

type Mode = "full" | "select";

const COMPONENT_OPTIONS: { value: client.RerunComponent; label: string }[] = [
  { value: "sov", label: "SOV / address parsing" },
  { value: "loss_run", label: "Loss run parsing" },
  { value: "policy_terms", label: "Limits, deductibles & sublimits" },
  { value: "policy_questions", label: "Policy questions" },
];

type ReprocessDialogProps = {
  visible: boolean;
  onHide: () => void;
  // ``undefined`` means a full reprocess; otherwise the selected components.
  onConfirm: (components: client.RerunComponent[] | undefined) => void;
  isPending: boolean;
};

export const ReprocessDialog: React.FC<ReprocessDialogProps> = ({
  visible,
  onHide,
  onConfirm,
  isPending,
}) => {
  const [mode, setMode] = useState<Mode>("full");
  const [selected, setSelected] = useState<client.RerunComponent[]>([]);

  const toggle = (component: client.RerunComponent, checked: boolean) => {
    setSelected((prev) =>
      checked
        ? [...prev, component]
        : prev.filter((value) => value !== component)
    );
  };

  const confirmDisabled =
    isPending || (mode === "select" && selected.length === 0);

  const footer = (
    <div className={styles.footer}>
      <Button
        label="Cancel"
        severity="secondary"
        text
        onClick={onHide}
        disabled={isPending}
      />
      <Button
        label="Reprocess"
        icon="pi pi-replay"
        loading={isPending}
        disabled={confirmDisabled}
        onClick={() => onConfirm(mode === "full" ? undefined : selected)}
      />
    </div>
  );

  return (
    <Dialog
      header="Reprocess submission"
      visible={visible}
      onHide={onHide}
      draggable={false}
      footer={footer}
      style={{ width: "30rem" }}
    >
      <div className={styles.container}>
        <div className={styles.option}>
          <RadioButton
            inputId="reprocess-full"
            checked={mode === "full"}
            onChange={() => setMode("full")}
          />
          <label htmlFor="reprocess-full" className={styles.optionLabel}>
            <div className={styles.optionTitle}>Full reprocess</div>
            <div className={styles.optionDescription}>
              Re-parse the entire email and every attachment.
            </div>
          </label>
        </div>

        <div className={styles.option}>
          <RadioButton
            inputId="reprocess-select"
            checked={mode === "select"}
            onChange={() => setMode("select")}
          />
          <label htmlFor="reprocess-select" className={styles.optionLabel}>
            <div className={styles.optionTitle}>Select components</div>
            <div className={styles.optionDescription}>
              Only re-run the chosen components; everything else is preserved.
            </div>
          </label>
        </div>

        {mode === "select" && (
          <div className={styles.componentList}>
            {COMPONENT_OPTIONS.map((option) => (
              <div key={option.value} className={styles.componentItem}>
                <Checkbox
                  inputId={`reprocess-${option.value}`}
                  checked={selected.includes(option.value)}
                  onChange={(e) => toggle(option.value, !!e.checked)}
                />
                <label htmlFor={`reprocess-${option.value}`}>
                  {option.label}
                </label>
              </div>
            ))}
          </div>
        )}
      </div>
    </Dialog>
  );
};
